import React from 'react'
import { Mutation } from 'react-apollo'
import { DO_SSO } from '../graphql/mutations'

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID

// Dynamically load the Google Identity Services script
let googleScriptLoaded = false
function loadGoogleScript() {
  if (googleScriptLoaded) return Promise.resolve()
  return new Promise((resolve, reject) => {
    if (document.getElementById('google-gis-script')) {
      googleScriptLoaded = true
      resolve()
      return
    }
    const script = document.createElement('script')
    script.id = 'google-gis-script'
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = () => { googleScriptLoaded = true; resolve() }
    script.onerror = reject
    document.head.appendChild(script)
  })
}

// Dynamically load the Facebook SDK
let fbScriptLoaded = false
function loadFacebookScript() {
  if (fbScriptLoaded) return Promise.resolve()
  return new Promise((resolve, reject) => {
    if (document.getElementById('facebook-jssdk')) {
      fbScriptLoaded = true
      resolve()
      return
    }
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: 'v18.0'
      })
      fbScriptLoaded = true
      resolve()
    }
    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.async = true
    script.onerror = reject
    document.head.appendChild(script)
  })
}

// Dynamically load the Apple Sign In JS SDK
let appleScriptLoaded = false
function loadAppleScript() {
  if (appleScriptLoaded) return Promise.resolve()
  return new Promise((resolve, reject) => {
    if (document.getElementById('apple-signin-script')) {
      appleScriptLoaded = true
      resolve()
      return
    }
    const script = document.createElement('script')
    script.id = 'apple-signin-script'
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    script.async = true
    script.onload = () => { appleScriptLoaded = true; resolve() }
    script.onerror = reject
    document.head.appendChild(script)
  })
}

class SSOButtons extends React.PureComponent {
  state = {
    loading: null,
    error: null
  }

  handleGoogle = (doSSO) => {
    if (!GOOGLE_CLIENT_ID) {
      this.setState({ error: 'Google sign-in is not configured.' })
      return
    }

    this.setState({ loading: 'google', error: null })

    loadGoogleScript()
      .then(() => {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          callback: (response) => {
            if (response.error) {
              this.setState({ loading: null, error: 'Google sign-in was cancelled.' })
              return
            }
            doSSO({
              variables: {
                driver: 'google',
                accessToken: response.access_token
              }
            }).catch(() => {
              this.setState({ loading: null })
            })
          },
          error_callback: () => {
            this.setState({ loading: null, error: 'Google sign-in failed. Please try again.' })
          }
        })
        client.requestAccessToken()
      })
      .catch(() => {
        this.setState({ loading: null, error: 'Failed to load Google sign-in.' })
      })
  }

  handleFacebook = (doSSO) => {
    if (!FACEBOOK_APP_ID) {
      this.setState({ error: 'Facebook sign-in is not configured.' })
      return
    }

    this.setState({ loading: 'facebook', error: null })

    loadFacebookScript()
      .then(() => {
        window.FB.login(function (response) {
          if (response.authResponse) {
            doSSO({
              variables: {
                driver: 'facebook',
                accessToken: response.authResponse.accessToken
              }
            }).catch(() => {
              this.setState({ loading: null })
            })
          } else {
            this.setState({ loading: null, error: 'Facebook sign-in was cancelled.' })
          }
        }.bind(this), { scope: 'email,public_profile' })
      })
      .catch(() => {
        this.setState({ loading: null, error: 'Failed to load Facebook sign-in.' })
      })
  }

  handleApple = (doSSO) => {
    if (!APPLE_CLIENT_ID) {
      this.setState({ error: 'Apple sign-in is not configured.' })
      return
    }

    this.setState({ loading: 'apple', error: null })

    loadAppleScript()
      .then(() => {
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI || 'https://develop.api.vevacollect.com/auth/apple/callback',
          usePopup: true
        })
        return window.AppleID.auth.signIn()
      })
      .then((response) => {
        const idToken = response.authorization.id_token
        const firstName = response.user && response.user.name ? response.user.name.firstName : undefined
        const lastName = response.user && response.user.name ? response.user.name.lastName : undefined

        doSSO({
          variables: {
            driver: 'apple',
            accessToken: idToken,
            firstName,
            lastName
          }
        }).catch(() => {
          this.setState({ loading: null })
        })
      })
      .catch((err) => {
        if (err && err.error === 'popup_closed_by_user') {
          this.setState({ loading: null })
          return
        }
        this.setState({ loading: null, error: 'Apple sign-in failed. Please try again.' })
      })
  }

  render() {
    const { onSuccess, onAuthenticating } = this.props
    const { loading, error } = this.state
    const label = 'Continue'

    return (
      <Mutation
        mutation={DO_SSO}
        update={(cache, { data: { doSSO } }) => {
          this.setState({ loading: null })

          if (!doSSO.success) {
            this.setState({ error: doSSO.message || 'Authentication failed. Please try again.' })
            return
          }

          if (doSSO.twoFactor) {
            if (this.props.on2FA) {
              this.props.on2FA(doSSO.accessToken)
            }
            return
          }

          localStorage.setItem('authToken', doSSO.accessToken)

          // Flag new users for onboarding before the route tree switches
          if (doSSO.isNewUser) {
            localStorage.setItem('sso_needs_onboarding', 'true')
          }

          // Signal loading state before auth transition
          if (onAuthenticating) {
            onAuthenticating()
          }

          if (onSuccess) {
            onSuccess(doSSO)
          }

          cache.writeData({
            data: {
              authStatus: {
                __typename: 'authStatus',
                status: 'loggedIn'
              }
            }
          })
        }}
      >
        {(doSSO) => (
          <div className='sso-buttons'>
            {error && <div className='sso-error'>{error}</div>}
            {GOOGLE_CLIENT_ID && (
              <button
                type='button'
                className='sso-button sso-button--google'
                disabled={!!loading}
                onClick={() => this.handleGoogle(doSSO)}
              >
                <svg className='sso-button__icon' viewBox='0 0 24 24' width='20' height='20'>
                  <path fill='#4285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z'/>
                  <path fill='#34A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/>
                  <path fill='#FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/>
                  <path fill='#EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/>
                </svg>
                {loading === 'google' ? 'Connecting...' : `${label} with Google`}
              </button>
            )}
            {FACEBOOK_APP_ID && (
              <button
                type='button'
                className='sso-button sso-button--facebook'
                disabled={!!loading}
                onClick={() => this.handleFacebook(doSSO)}
              >
                <svg className='sso-button__icon' viewBox='0 0 24 24' width='20' height='20'>
                  <path fill='#1877F2' d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z'/>
                </svg>
                {loading === 'facebook' ? 'Connecting...' : `${label} with Facebook`}
              </button>
            )}
            {APPLE_CLIENT_ID && (
              <button
                type='button'
                className='sso-button sso-button--apple'
                disabled={!!loading}
                onClick={() => this.handleApple(doSSO)}
              >
                <svg className='sso-button__icon' viewBox='0 0 24 24' width='20' height='20'>
                  <path fill='#FFFFFF' d='M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.23 0-1.44.62-2.2.44-3.06-.4C3.79 16.17 4.36 9.53 8.8 9.29c1.28.06 2.15.72 2.9.76.97-.2 1.9-.9 2.98-.81 1.41.11 2.48.66 3.18 1.67-2.86 1.72-2.18 5.52.49 6.58-.6 1.53-1.37 3.04-2.3 3.79zM12.05 9.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z'/>
                </svg>
                {loading === 'apple' ? 'Connecting...' : `${label} with Apple`}
              </button>
            )}
          </div>
        )}
      </Mutation>
    )
  }
}

export default SSOButtons
